/**
 * MetricasLeadTime.jsx · v101.179.0 · Módulo Métricas · Lead Time (diseño aprobado: neumorfismo de la plataforma).
 * Vistas: Horario operativo · B2B · B2C · Interno · Asistencias (dashboard) · Evolución · Detalle (reportería).
 * Datos: /api/v1/metricas/lead-time/* (solo lectura). Excel y envío programado por correo.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Download, CalendarClock, Loader2, ChevronDown, Search, Trash2, Send, AlertTriangle } from "lucide-react";

/* ── Tokens del tema (index.css) ── */
const C = { bg: "#E4EAF0", surf: "#ECF0F5", ink: "#0A3747", text: "#1E293B", sub: "#475569", mute: "#64748B",
  lime: "#E0E621", info: "#0284C7", warn: "#D97706", bad: "#DC2626", ok: "#16A34A", teal: "#5D8B96" };
const OUT = "-6px -6px 14px rgba(255,255,255,.95), 6px 6px 14px rgba(163,177,198,.55)";
const OUT_LG = "-10px -10px 22px rgba(255,255,255,.95), 10px 10px 22px rgba(163,177,198,.55)";
const OUT_SM = "-3px -3px 8px rgba(255,255,255,.95), 3px 3px 8px rgba(163,177,198,.55)";
const IN = "inset -3px -3px 7px rgba(255,255,255,.95), inset 3px 3px 7px rgba(163,177,198,.55)";
const IN_SM = "inset -2px -2px 5px rgba(255,255,255,.95), inset 2px 2px 5px rgba(163,177,198,.55)";
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const card = { padding: 24, borderRadius: 26, background: C.surf, boxShadow: OUT_LG, display: "flex", flexDirection: "column", gap: 16, minWidth: 0 };
const well = { borderRadius: 16, background: C.bg, boxShadow: IN_SM };
const h2 = { fontSize: 14, fontWeight: 700, color: C.ink };
const cap = { fontSize: 12, color: C.sub };

const VIEWS = [
  { id: "general", label: "Horario operativo" }, { id: "b2b", label: "B2B" }, { id: "b2c", label: "B2C" },
  { id: "interno", label: "Interno" }, { id: "asistencias", label: "Asistencias" },
  { id: "cumplimiento", label: "Cumplimiento HL y técnicos" },   // v101.268.0
  { id: "evolucion", label: "Evolución" }, { id: "detalle", label: "Detalle" },
];
const FILTERS = [
  ["sucursal", "Sucursal"], ["tecnico", "Técnico"], ["tipo_tecnico", "Tipo de técnico"],
  ["categoria", "Categoría de actividad"], ["tipo_cliente", "Tipo de cliente"], ["medio", "Medio de transmisión"],
];
const ltColor = (h) => (h == null ? "#CBD5E1" : h <= 6 ? C.ink : h <= 12 ? C.info : h <= 24 ? C.warn : C.bad);
const heatScale = ["#0A3747", "#2F5F6E", "#5D8B96", "#A9C08A", "#E0E621", "#F59E0B", "#E1582A", "#DC2626"];
const f1 = (v, s = "") => (v == null ? "—" : `${Number(v).toFixed(1)}${s}`);
const fp = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);
const fn = (v) => (v == null ? "—" : Number(v).toLocaleString("en-US"));
const iso = (d) => d.toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return iso(new Date(d.getFullYear(), d.getMonth(), 1)); };
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

/* ── Piezas ── */
function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{ border: 0, cursor: "pointer", fontFamily: "Inter", fontSize: 13, padding: "10px 16px", borderRadius: 13,
      fontWeight: active ? 700 : 600, color: active ? C.ink : C.sub, background: active ? C.surf : "transparent", boxShadow: active ? OUT_SM : "none" }}>
      {children}
    </button>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub, minWidth: 0 }}>
      {label}
      <span style={{ position: "relative", display: "block" }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} style={{ appearance: "none", width: "100%", border: 0, padding: "10px 32px 10px 12px",
          borderRadius: 12, fontSize: 13, fontWeight: 600, color: C.text, background: C.bg, boxShadow: IN_SM, fontFamily: "Inter" }}>
          <option value="">Todos</option>
          {(options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        <ChevronDown size={14} color={C.mute} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
      </span>
    </label>
  );
}

function Rings({ k }) {
  const rings = [["24 h", k.c24, C.ok, 84], ["12 h", k.c12, C.info, 67], ["9 h (HL)", k.c9, C.teal, 50], ["6 h (HL)", k.c6, C.ink, 33]];
  return (
    <section style={{ ...card, gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={h2}>Anillos de cumplimiento</span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: C.sub }}>{fn(k.ordenes)} órdenes</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
        <div style={{ position: "relative", width: 200, height: 200, borderRadius: "50%", background: C.surf, boxShadow: "-8px -8px 18px rgba(255,255,255,.95), 8px 8px 18px rgba(163,177,198,.55)", flexShrink: 0 }}>
          <svg role="img" aria-label="Cumplimiento por meta" width="200" height="200" viewBox="0 0 200 200" style={{ transform: "rotate(-90deg)" }}>
            {rings.map(([lab, v, c, r]) => (
              <g key={lab}>
                <circle cx="100" cy="100" r={r} fill="none" stroke="#D9E1EA" strokeWidth="12" />
                <circle cx="100" cy="100" r={r} fill="none" stroke={c} strokeWidth="12" strokeLinecap="round" pathLength="100"
                  strokeDasharray={`${Math.max(0, Math.min(100, v || 0))} 100`} style={{ transition: "stroke-dasharray .8s cubic-bezier(.2,.8,.2,1)" }} />
              </g>
            ))}
          </svg>
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: C.lime, boxShadow: `0 0 0 4px ${C.surf}, 0 0 18px rgba(224,230,33,.9)`, animation: "lt-pulse 2.4s ease-in-out infinite" }} />
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 150, display: "flex", flexDirection: "column", gap: 10 }}>
          {rings.map(([lab, v, c]) => (
            <div key={lab} style={{ ...well, display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", borderRadius: 12 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 600, color: "#334155" }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: c, boxShadow: `0 0 8px ${c}` }} />Meta {lab}
              </span>
              <span style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>{fp(v)}</span>
            </div>
          ))}
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 11.5, color: C.sub }}>% de órdenes cerradas dentro de la meta. 6 h y 9 h se miden en el horario laboral configurado del técnico.</p>
    </section>
  );
}

function Hero({ k, prev }) {
  const total = k.lt || 0;
  const parts = [[k.lt_hl, C.ink, "Horario laboral"], [k.fuera_horario, C.teal, "Fuera de horario"], [k.tp, C.warn, "Tiempo pendiente"]];
  const sum = parts.reduce((a, p) => a + (p[0] || 0), 0) || 1;
  const delta = prev && prev.lt != null && k.lt != null ? k.lt - prev.lt : null;
  return (
    <section style={{ ...card, gap: 18 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.sub }}>Lead Time promedio</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 64, fontWeight: 800, letterSpacing: "-.04em", color: C.ink, lineHeight: 1 }}>{k.lt == null ? "—" : total.toFixed(1)}</span>
            <span style={{ fontSize: 18, fontWeight: 700, color: C.sub }}>horas</span>
          </div>
        </div>
        {delta != null && (
          <span style={{ fontSize: 12, fontWeight: 700, padding: "5px 10px", borderRadius: 999,
            color: delta <= 0 ? "#15803D" : "#B91C1C", background: delta <= 0 ? "#DCFCE7" : "#FEE2E2" }}>
            {delta <= 0 ? "−" : "+"}{Math.abs(delta).toFixed(1)} h vs periodo anterior
          </span>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".1em", color: C.sub }}>DESCOMPOSICIÓN DEL TIEMPO</div>
        <div style={{ display: "flex", height: 18, borderRadius: 999, overflow: "hidden", background: C.bg, boxShadow: IN_SM }}>
          {parts.map(([v, c, lab], i) => (
            <div key={lab} title={`${lab}: ${f1(v, " h")}`} style={{ width: `${((v || 0) / sum) * 100}%`, transition: "width .8s",
              background: i === 2 ? "repeating-linear-gradient(135deg,#D97706 0 6px,#F59E0B 6px 12px)" : c }} />
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 10 }}>
          {parts.map(([v, c, lab]) => (
            <div key={lab}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sub }}><span style={{ width: 8, height: 8, borderRadius: 3, background: c }} />{lab}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{f1(v, " h")}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ ...well, flex: 1, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, color: C.sub }}>Lead Time real <span style={{ color: C.mute }}>(sin pendiente)</span></div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.ink }}>{f1(k.lt_real, " h")}</div>
        </div>
        <div style={{ ...well, flex: 1, padding: "12px 14px" }}>
          <div style={{ fontSize: 11, color: C.sub }}>Cumple LTR 6 h</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: C.ink }}>{fp(k.ltr6)}</div>
        </div>
      </div>
    </section>
  );
}

function Kpis({ k }) {
  const items = [["Total órdenes", fn(k.ordenes), "tarea hecha = sí"], ["Clientes visitados", fn(k.clientes), "contratos distintos"],
    ["Revisitas", fn(k.revisitas), "órdenes − clientes"], ["Órdenes por cliente", k.ord_x_cliente ?? "—", "promedio"],
    ["Con T.P.", fp(k.pct_tp), `${fn(k.con_tp)} actividades`], ["Ritmo de soluciones", k.ritmo ?? "—", "órdenes ÷ LT prom."]];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 16 }}>
      {items.map(([l, v, h]) => (
        <div key={l} style={{ padding: 16, borderRadius: 20, background: C.surf, boxShadow: "-5px -5px 12px rgba(255,255,255,.95), 5px 5px 12px rgba(163,177,198,.55)", display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.sub }}>{l}</span>
          <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-.02em", color: C.ink }}>{v}</span>
          <span style={{ fontSize: 10.5, color: C.mute }}>{h}</span>
        </div>
      ))}
    </div>
  );
}

function Spectrum({ bins, k }) {
  const max = Math.max(1, ...bins.map((b) => b.n));
  const col = (i) => (i < 2 ? C.ink : i < 4 ? C.info : i < 6 ? C.warn : C.bad);
  const marks = { 1: `6 h · ${fp(k.c6)}`, 2: `9 h · ${fp(k.c9)}`, 3: `12 h · ${fp(k.c12)}`, 5: `24 h · ${fp(k.c24)}` };
  return (
    <section style={card}>
      <div>
        <div style={h2}>Espectro de Lead Time</div>
        <div style={cap}>Cuántas órdenes cierran en cada franja de horas · las marcas son las metas (6 y 9 h en horario laboral)</div>
      </div>
      <div style={{ position: "relative", height: 230, padding: "22px 12px 0", borderRadius: 18, background: C.bg, boxShadow: IN, display: "flex", alignItems: "flex-end", gap: 12 }}>
        {bins.map((b, i) => (
          <div key={b.label} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", gap: 6, position: "relative", minWidth: 0 }}>
            <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 600, color: "#334155" }}>{fn(b.n)}</span>
            <div title={`${b.label}: ${b.n}`} style={{ width: "100%", maxWidth: 64, height: Math.round((b.n / max) * 150), borderRadius: "10px 10px 4px 4px",
              background: `linear-gradient(180deg, ${col(i)}, ${col(i)}cc)`, boxShadow: `0 6px 14px ${col(i)}40`, transition: "height .8s cubic-bezier(.2,.8,.2,1)" }} />
            <span style={{ fontSize: 11, fontWeight: 600, color: C.sub, paddingBottom: 10, whiteSpace: "nowrap" }}>{b.label}</span>
            {marks[i] && (
              <>
                <div style={{ position: "absolute", right: -8, top: 0, bottom: 30, borderRight: `2px dashed ${C.ink}`, opacity: 0.55 }} />
                <span style={{ position: "absolute", right: -44, top: -18, fontFamily: MONO, fontSize: 10.5, fontWeight: 700, color: C.ink, background: C.lime, padding: "2px 6px", borderRadius: 6, whiteSpace: "nowrap", zIndex: 1 }}>{marks[i]}</span>
              </>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Gauges({ familias }) {
  const worst = [...familias].sort((a, b) => (b.fuera24 || 0) - (a.fuera24 || 0))[0];
  const tot24 = familias.reduce((a, f) => a + (f.fuera24 || 0), 0);
  const colors = { Instalaciones: C.warn, "Asistencia técnica": C.info, "Servicios adicionales": C.teal };
  return (
    <section style={card}>
      <div>
        <div style={h2}>Lead Time por tipo de trabajo</div>
        <div style={cap}>Promedio en horas · arco contra la meta de 24 h</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12 }}>
        {familias.map((g) => (
          <div key={g.name} style={{ ...well, borderRadius: 18, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "14px 6px" }}>
            <svg role="img" aria-label={`${g.name}: ${f1(g.lt, " horas")}`} width="120" height="72" viewBox="0 0 120 72">
              <path d="M 12 62 A 48 48 0 0 1 108 62" fill="none" stroke="#D3DCE6" strokeWidth="12" strokeLinecap="round" />
              <path d="M 12 62 A 48 48 0 0 1 108 62" fill="none" stroke={colors[g.name] || C.ink} strokeWidth="12" strokeLinecap="round" pathLength="100"
                strokeDasharray={`${Math.min(100, Math.round(((g.lt || 0) / 24) * 100))} 100`} />
            </svg>
            <span style={{ fontSize: 22, fontWeight: 800, color: C.ink, marginTop: -18 }}>{f1(g.lt, " h")}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#334155", textAlign: "center" }}>{g.name}</span>
            <span style={{ fontFamily: MONO, fontSize: 10.5, color: C.sub }}>{fn(g.n)} órdenes</span>
          </div>
        ))}
      </div>
      {worst && tot24 > 0 && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "12px 14px", borderRadius: 16, background: "#FBFCE0" }}>
          <AlertTriangle size={18} color="#6B5A00" />
          <span style={{ fontSize: 12, color: "#4A3F00" }}>{worst.name} concentra el {Math.round((worst.fuera24 / tot24) * 100)}% de las órdenes de estos tipos fuera de 24 h.</span>
        </div>
      )}
    </section>
  );
}

function Pulse({ heat }) {
  const hours = (heat[0]?.cells || []).map((c) => c.h);
  return (
    <section style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={h2}>Pulso operativo</div>
          <div style={cap}>Lead Time promedio según el día y la hora en que se creó la tarea</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.sub }}>
          rápido<span style={{ width: 120, height: 10, borderRadius: 999, background: `linear-gradient(90deg, ${heatScale.join(",")})` }} />lento
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: 560, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "grid", gridTemplateColumns: `44px repeat(${hours.length}, minmax(0,1fr))`, gap: 6 }}>
            <span />
            {hours.map((h) => <span key={h} style={{ fontFamily: MONO, fontSize: 10, color: C.sub, textAlign: "center" }}>{String(h).padStart(2, "0")}</span>)}
          </div>
          {heat.map((row) => (
            <div key={row.day} style={{ display: "grid", gridTemplateColumns: `44px repeat(${hours.length}, minmax(0,1fr))`, gap: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#334155", alignSelf: "center" }}>{row.day}</span>
              {row.cells.map((c) => {
                const idx = c.lt == null ? -1 : Math.max(0, Math.min(7, Math.floor((c.lt - 3) / 4)));
                return (
                  <span key={c.h} title={c.lt == null ? "sin órdenes" : `${row.day} ${c.h}:00 · ${f1(c.lt, " h")} · ${c.n} órdenes`}
                    style={{ height: 30, borderRadius: 8, display: "grid", placeItems: "center", fontFamily: MONO, fontSize: 10.5, fontWeight: 600,
                      color: idx < 0 ? C.mute : idx < 3 || idx > 5 ? "#FFFFFF" : C.text, background: idx < 0 ? "#DCE3EA" : heatScale[idx] }}>
                    {c.lt == null ? "·" : Math.round(c.lt)}
                  </span>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Constellation({ techs }) {
  const list = techs.slice(0, 18).filter((t) => t.lt != null);
  if (!list.length) return <section style={card}><div style={h2}>Constelación de técnicos</div><div style={cap}>Sin datos.</div></section>;
  const maxN = Math.max(...list.map((t) => t.ordenes)), minN = Math.min(...list.map((t) => t.ordenes));
  const maxLt = Math.max(24, ...list.map((t) => t.lt));
  const avgN = list.reduce((a, t) => a + t.ordenes, 0) / list.length;
  const avgLt = list.reduce((a, t) => a + t.lt, 0) / list.length;
  const X = (n) => 6 + ((n - minN) / Math.max(1, maxN - minN)) * 88;
  const Y = (h) => 92 - (h / maxLt) * 84;
  return (
    <section style={{ ...card, gap: 14 }}>
      <div>
        <div style={h2}>Constelación de técnicos</div>
        <div style={cap}>Órdenes cerradas (→) vs Lead Time promedio (↑) · tamaño = % con T.P.</div>
      </div>
      <div style={{ position: "relative", height: 300, borderRadius: 18, overflow: "hidden", boxShadow: IN,
        background: "radial-gradient(circle at 80% 85%, rgba(22,163,74,.12), transparent 45%), radial-gradient(circle at 15% 15%, rgba(220,38,38,.08), transparent 45%), #E4EAF0" }}>
        <div style={{ position: "absolute", left: `${X(avgN)}%`, top: 8, bottom: 8, borderLeft: "1.5px dashed rgba(10,55,71,.3)" }} />
        <div style={{ position: "absolute", top: `${Y(avgLt)}%`, left: 8, right: 8, borderTop: "1.5px dashed rgba(10,55,71,.3)" }} />
        <span style={{ position: "absolute", right: 12, bottom: 10, fontSize: 10.5, fontWeight: 700, color: "#15803D" }}>muchas y rápidas ↘ ideal</span>
        <span style={{ position: "absolute", left: 12, top: 10, fontSize: 10.5, fontWeight: 700, color: "#B91C1C" }}>pocas y lentas</span>
        {list.map((t, i) => {
          const size = 24 + Math.min(30, (t.pct_tp || 0) * 0.9);
          const c = ltColor(t.lt);
          return (
            <div key={t.name} title={`${t.name} · ${t.ordenes} órdenes · ${f1(t.lt, " h")} · T.P. ${fp(t.pct_tp)}`}
              style={{ position: "absolute", left: `${X(t.ordenes)}%`, top: `${Y(t.lt)}%`, transform: "translate(-50%,-50%)", width: size, height: size,
                borderRadius: "50%", display: "grid", placeItems: "center", fontFamily: MONO, fontSize: 9.5, fontWeight: 700, color: "#FFF",
                background: `radial-gradient(circle at 30% 30%, ${c}cc, ${c})`, boxShadow: `0 0 0 3px ${C.surf}, 0 8px 18px ${c}55`, cursor: "default" }}>
              {(t.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || i + 1}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Branches({ list }) {
  return (
    <section style={{ ...card, gap: 12 }}>
      <div style={h2}>Sucursales</div>
      {list.map((s) => (
        <div key={s.name} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px minmax(0,1fr) 60px", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.name}>{s.name}</span>
            <div style={{ height: 14, borderRadius: 999, background: C.bg, boxShadow: IN_SM, position: "relative" }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, ((s.lt || 0) / 24) * 100)}%`, borderRadius: 999,
                background: `linear-gradient(90deg, ${C.ink}, ${(s.lt || 0) > 15 ? C.warn : C.info})`, boxShadow: "0 4px 10px rgba(10,55,71,.25)" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 800, color: C.ink, textAlign: "right" }}>{f1(s.lt, " h")}</span>
          </div>
          <div style={{ display: "flex", gap: 14, marginLeft: 132, fontFamily: MONO, fontSize: 10.5, color: C.sub }}>
            <span>{fn(s.ordenes)} órdenes</span><span>24 h: {fp(s.c24)}</span><span>12 h: {fp(s.c12)}</span>
          </div>
        </div>
      ))}
      {!list.length && <div style={cap}>Sin datos.</div>}
    </section>
  );
}

function Techs({ list, onDetail }) {
  return (
    <section style={{ ...card, gap: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={h2}>Técnicos · contra la meta de 12 h</div>
        <button onClick={onDetail} style={{ border: 0, background: "transparent", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.ink }}>Ver detalle completo →</button>
      </div>
      <div style={{ overflowX: "auto", maxHeight: 420 }}>
        <div style={{ minWidth: 560, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "grid", gridTemplateColumns: "170px 70px minmax(0,1fr) 64px 64px", gap: 12, fontFamily: MONO, fontSize: 10.5, letterSpacing: ".06em", color: C.sub }}>
            <span>TÉCNICO</span><span>ÓRDENES</span><span>LEAD TIME (0–24 h)</span><span>12 H</span><span>T.P.</span>
          </div>
          {list.slice(0, 15).map((t) => (
            <div key={t.name} style={{ ...well, borderRadius: 12, display: "grid", gridTemplateColumns: "170px 70px minmax(0,1fr) 64px 64px", gap: 12, alignItems: "center", padding: "8px 10px" }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.name}>{t.name}</span>
              <span style={{ fontFamily: MONO, fontSize: 12, color: "#334155" }}>{fn(t.ordenes)}</span>
              <div style={{ position: "relative", height: 10, borderRadius: 999, background: "#D9E1EA" }}>
                <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, ((t.lt || 0) / 24) * 100)}%`, borderRadius: 999, background: ltColor(t.lt) }} />
                <div style={{ position: "absolute", left: "50%", top: -4, bottom: -4, width: 2, background: C.ink }} />
              </div>
              <span style={{ fontSize: 12.5, fontWeight: 800, color: C.ink }}>{fp(t.c12)}</span>
              <span style={{ fontSize: 12, color: C.sub }}>{fp(t.pct_tp)}</span>
            </div>
          ))}
          {!list.length && <div style={cap}>Sin datos.</div>}
        </div>
      </div>
    </section>
  );
}

/* ── Evolución ── */
function Evolution({ data }) {
  if (!data?.serie?.length) return <section style={card}><div style={cap}>Sin datos para el periodo.</div></section>;
  const S = data.serie, n = S.length, W = 1100, H = 320;
  const X = (i) => (n === 1 ? W / 2 : (i * W) / (n - 1));
  const Y = (p) => H - ((p || 0) / 100) * H;
  const band = (lo, hi) => [...hi.map((v, i) => `${X(i)},${Y(v)}`), ...lo.map((v, i) => `${X(i)},${Y(v)}`).reverse()].join(" ");
  const c6 = S.map((s) => s.c6 || 0), c12 = S.map((s) => Math.max(s.c12 || 0, s.c6 || 0)), c24 = S.map((s) => Math.max(s.c24 || 0, s.c12 || 0));
  const zero = S.map(() => 0), full = S.map(() => 100);
  const maxLt = Math.max(24, ...S.map((s) => s.lt || 0));
  const ly = (h) => H - ((h || 0) / maxLt) * H;
  const line = S.map((s, i) => `${X(i)},${ly(s.lt)}`).join(" ");
  const first = S[0], last = S[n - 1];
  const chip = (good) => ({ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 999, color: good ? "#15803D" : "#B91C1C", background: good ? "#DCFCE7" : "#FEE2E2" });
  const deltas = [
    ["Lead Time promedio", f1(last.lt, " h"), last.lt - first.lt, " h", true],
    ["Cumplimiento 6 h (HL)", fp(last.c6), (last.c6 || 0) - (first.c6 || 0), " pts", false],
    ["Cumplimiento 12 h", fp(last.c12), (last.c12 || 0) - (first.c12 || 0), " pts", false],
    ["Órdenes > 24 h", fp(100 - (last.c24 || 0)), (first.c24 || 0) - (last.c24 || 0), " pts", true],
  ];
  const D = data.duel || {};
  const duelRows = [["Lead Time prom.", "lt", "h", true], ["Cumple 6 h", "c6", "%", false], ["Cumple 12 h", "c12", "%", false],
    ["Cumple 24 h", "c24", "%", false], ["Con T.P.", "pct_tp", "%", true], ["Órdenes", "ordenes", "", false]];
  const mesLbl = (m) => new Date(`${m}-15`).toLocaleDateString("es-EC", { month: "short", year: "2-digit" });
  return (
    <>
      <section style={{ ...card, padding: 26 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Río del Lead Time</div>
            <div style={cap}>Bandas: % de órdenes por franja de cierre (eje izq.) · línea amarilla: Lead Time promedio (eje der.)</div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11, color: C.sub }}>
            {[["≤ 6 h", C.ink], ["6–12 h", C.info], ["12–24 h", "#F59E0B"], ["> 24 h", C.bad]].map(([l, c]) => (
              <span key={l} style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: c }} />{l}</span>
            ))}
            <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 16, height: 3, borderRadius: 3, background: C.lime }} />LT promedio</span>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "40px minmax(0,1fr) 44px", gap: 8 }}>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: MONO, fontSize: 10, color: C.sub, padding: "4px 0 26px" }}>
            {["100%", "75%", "50%", "25%", "0%"].map((x) => <span key={x}>{x}</span>)}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
            <div style={{ position: "relative", height: 320, borderRadius: 18, overflow: "hidden", background: C.bg, boxShadow: IN }}>
              <svg role="img" aria-label="Evolución mensual" width="100%" height="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
                <polygon points={band(c24, full)} fill={C.bad} opacity=".85" />
                <polygon points={band(c12, c24)} fill="#F59E0B" opacity=".9" />
                <polygon points={band(c6, c12)} fill={C.info} opacity=".92" />
                <polygon points={band(zero, c6)} fill={C.ink} />
                <polyline points={line} fill="none" stroke="#C4CA12" strokeWidth="9" opacity=".35" vectorEffect="non-scaling-stroke" />
                <polyline points={line} fill="none" stroke={C.lime} strokeWidth="3.5" vectorEffect="non-scaling-stroke" />
              </svg>
              {S.map((s, i) => (
                <div key={s.mes} title={`${s.mes}: ${f1(s.lt, " h")} · ${fn(s.ordenes)} órdenes`} style={{ position: "absolute", left: `${(X(i) / W) * 100}%`, top: `${(ly(s.lt) / H) * 100}%`,
                  width: 12, height: 12, margin: "-6px 0 0 -6px", borderRadius: "50%", background: C.lime, boxShadow: `0 0 0 3px ${C.ink}, 0 0 12px ${C.lime}` }}>
                  <span style={{ position: "absolute", left: "50%", bottom: 16, transform: "translateX(-50%)", fontFamily: MONO, fontSize: 10.5, fontWeight: 700, color: C.ink, background: "#FBFCE0", padding: "1px 5px", borderRadius: 6, whiteSpace: "nowrap" }}>{f1(s.lt)}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${n}, minmax(0,1fr))`, fontFamily: MONO, fontSize: 10.5, color: C.sub, textAlign: "center" }}>
              {S.map((s) => <span key={s.mes}>{mesLbl(s.mes)}</span>)}
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", fontFamily: MONO, fontSize: 10, color: "#6B5A00", padding: "4px 0 26px", textAlign: "right" }}>
            {[maxLt, maxLt * 0.75, maxLt * 0.5, maxLt * 0.25, 0].map((x) => <span key={x}>{Math.round(x)} h</span>)}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
          {deltas.map(([l, v, d, u, lowerBetter]) => (
            <div key={l} style={{ ...well, padding: "14px 16px" }}>
              <div style={{ fontSize: 11, color: C.sub }}>{l}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: C.ink }}>{v}</span>
                {Number.isFinite(d) && <span style={chip(lowerBetter ? d <= 0 : d >= 0)}>{d > 0 ? "+" : d < 0 ? "−" : ""}{Math.abs(d).toFixed(1)}{u}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 22 }}>
        <section style={card}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Duelo B2B vs B2C</div>
            <span style={{ fontSize: 11, color: C.sub }}>mejor lado resaltado</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px minmax(0,1fr)", gap: 10 }}>
            <div style={{ textAlign: "right" }}><div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>B2B</div><div style={{ fontSize: 10.5, color: C.sub }}>Corporativo público · Cuentas clave · Empresas · Mi Pyme</div></div>
            <span />
            <div><div style={{ fontSize: 13, fontWeight: 800, color: C.info }}>B2C</div><div style={{ fontSize: 10.5, color: C.sub }}>Residencial · Mi Negocio · Otro · sin categoría</div></div>
          </div>
          {duelRows.map(([lab, key, u, lowerBetter]) => {
            const a = D.B2B?.[key], b = D.B2C?.[key];
            const max = Math.max(a || 0, b || 0) || 1;
            const aWins = a != null && b != null && (lowerBetter ? a < b : a > b);
            const fmt = (v) => (v == null ? "—" : u === "h" ? f1(v, " h") : u === "%" ? fp(v) : fn(v));
            const bar = (v, win, c) => ({ height: "100%", width: `${((v || 0) / max) * 100}%`, borderRadius: 999, background: c, opacity: win ? 1 : 0.45, boxShadow: win ? `0 4px 12px ${c}66` : "none" });
            return (
              <div key={key} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px minmax(0,1fr)", gap: 10, alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>{fmt(a)}</span>
                  <div style={{ flex: 1, display: "flex", justifyContent: "flex-end", height: 16, borderRadius: 999, background: C.bg, boxShadow: IN_SM }}><div style={bar(a, aWins, C.ink)} /></div>
                </div>
                <span style={{ textAlign: "center", fontSize: 11.5, fontWeight: 600, color: "#334155" }}>{lab}</span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ flex: 1, height: 16, borderRadius: 999, background: C.bg, boxShadow: IN_SM }}><div style={bar(b, !aWins, C.info)} /></div>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.info }}>{fmt(b)}</span>
                </div>
              </div>
            );
          })}
        </section>
        <div style={{ display: "grid", gap: 22 }}>
          {[["Interno", "Clasificación «INTERNAS»", data.scopes?.interno, C.ok, "c24", "24 h"], ["Asistencias", "Asistencia técnica", data.scopes?.asistencias, C.info, "c12", "12 h"]].map(([t, sc, m, c, key, meta]) => (
            <section key={t} style={{ ...card, flexDirection: "row", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
              <div style={{ position: "relative", width: 120, height: 120, flexShrink: 0, borderRadius: "50%", background: C.surf, boxShadow: OUT }}>
                <svg role="img" aria-label={`${t}: ${fp(m?.[key])} cumple ${meta}`} width="120" height="120" viewBox="0 0 120 120" style={{ transform: "rotate(-90deg)" }}>
                  <circle cx="60" cy="60" r="46" fill="none" stroke="#D9E1EA" strokeWidth="12" />
                  <circle cx="60" cy="60" r="46" fill="none" stroke={c} strokeWidth="12" strokeLinecap="round" pathLength="100" strokeDasharray={`${m?.[key] || 0} 100`} />
                </svg>
                <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center" }}>
                  <div><div style={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{fp(m?.[key])}</div><div style={{ fontSize: 10, color: C.sub }}>meta {meta}</div></div>
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 200, display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: C.ink }}>{t}</div>
                <div style={{ fontSize: 11.5, color: C.sub }}>{sc}</div>
                <div style={{ display: "flex", gap: 16 }}>
                  {[["Lead Time", f1(m?.lt, " h")], ["Horario laboral", f1(m?.lt_hl, " h")], ["Órdenes", fn(m?.ordenes)]].map(([l, v]) => (
                    <div key={l}><div style={{ fontSize: 10.5, color: C.sub }}>{l}</div><div style={{ fontSize: 18, fontWeight: 800, color: C.ink }}>{v}</div></div>
                  ))}
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>

      <section style={card}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Tendencia por sucursal</div>
          <div style={cap}>Lead Time promedio por mes · la banda gris es la meta de 12 h</div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 16 }}>
          {(data.multiples || []).map((m) => {
            const pts = m.serie.map((p, i) => (p.lt == null ? null : `${(i * 220) / Math.max(1, m.serie.length - 1)},${64 - (Math.min(p.lt, 24) / 24) * 64}`)).filter(Boolean).join(" ");
            const vals = m.serie.filter((p) => p.lt != null);
            const d = vals.length > 1 ? vals[vals.length - 1].lt - vals[0].lt : null;
            const lastV = vals.length ? vals[vals.length - 1].lt : null;
            return (
              <div key={m.name} style={{ ...well, borderRadius: 18, padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{m.name}</span>
                  {d != null && <span style={chip(d <= 0)}>{d > 0 ? "+" : "−"}{Math.abs(d).toFixed(1)} h</span>}
                </div>
                <div style={{ fontSize: 24, fontWeight: 800, color: C.ink }}>{f1(lastV, " h")}</div>
                <svg role="img" aria-label={`${m.name}: tendencia`} width="100%" height="64" viewBox="0 0 220 64" preserveAspectRatio="none">
                  <rect x="0" y="32" width="220" height="32" fill="#D9E1EA" opacity=".6" />
                  <polyline points={pts} fill="none" stroke={(lastV || 0) > 15 ? C.warn : C.ink} strokeWidth="3" vectorEffect="non-scaling-stroke" />
                </svg>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

/* ── Reportería ── */
function Reporting({ params, opts }) {
  const [filas, setFilas] = useState("sucursal");
  const [columnas, setColumnas] = useState("categoria");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sched, setSched] = useState([]);
  const [form, setForm] = useState({ nombre: "Lead Time semanal", frecuencia: "semanal", dia: 0, hora: "07:30", destinatarios: "" });
  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: d } = await api.get("/v1/metricas/lead-time/detalle", { params: { ...params, filas, columnas, q: q || undefined, page }, timeout: 180000 });
      setData(d);
    } catch (e) { toast.error(errMsg(e, "No se pudo cargar el detalle")); } finally { setBusy(false); }
  }, [params, filas, columnas, q, page]);
  const loadSched = useCallback(async () => {
    try { const { data: d } = await api.get("/v1/metricas/lead-time/programaciones"); setSched(d.items || []); } catch (_) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSched(); }, [loadSched]);
  const saveSched = async () => {
    try {
      const filtros = Object.fromEntries(Object.entries(params).filter(([k, v]) => v && FILTERS.some(([f]) => f === k)));
      await api.post("/v1/metricas/lead-time/programaciones", { ...form, vista: params.vista === "evolucion" || params.vista === "detalle" ? "general" : params.vista, filtros });
      toast.success("Envío programado guardado");
      loadSched();
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar la programación")); }
  };
  const groupOpts = [["sucursal", "Sucursal"], ["categoria", "Categoría"], ["tecnico", "Técnico"], ["tipo_cliente", "Tipo de cliente"], ["mes", "Mes"], ["tipo_tecnico", "Tipo de técnico"], ["medio", "Medio"], ["grupo", "B2B / B2C"]];
  const cellBg = (h) => (h == null ? ["#DCE3EA", C.mute] : h <= 8 ? [C.ink, "#FFF"] : h <= 12 ? ["#2F5F6E", "#FFF"] : h <= 16 ? [C.lime, C.text] : h <= 20 ? ["#F59E0B", C.text] : [C.bad, "#FFF"]);
  const M = data?.matriz;
  const Dt = data?.detalle;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 22, alignItems: "flex-start" }}>
      <main style={{ flex: "999 1 640px", minWidth: 0, display: "flex", flexDirection: "column", gap: 22 }}>
        <section style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Matriz</div>
              <div style={cap}>Cada celda: Lead Time promedio · debajo, % que cumple 12 h · color según el Lead Time</div>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {[["Filas", filas, setFilas], ["Columnas", columnas, setColumnas]].map(([l, v, set]) => (
                <label key={l} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#334155" }}>{l}
                  <select value={v} onChange={(e) => set(e.target.value)} style={{ border: 0, padding: "8px 10px", borderRadius: 10, background: C.bg, boxShadow: IN_SM, fontFamily: "Inter", fontSize: 12, fontWeight: 600 }}>
                    {groupOpts.map(([k, lab]) => <option key={k} value={k}>{lab}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </div>
          {busy && !data ? <div style={{ ...cap, display: "flex", gap: 8, alignItems: "center" }}><Loader2 size={14} className="animate-spin" />Calculando…</div> : M && (
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 160 + M.cols.length * 130, display: "grid", gridTemplateColumns: `160px repeat(${M.cols.length}, minmax(120px,1fr))`, gap: 8 }}>
                <span />
                {M.cols.map((c) => <span key={c} style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".04em", color: C.sub, textAlign: "center" }}>{String(c).toUpperCase()}</span>)}
                {M.rows.map((r) => (
                  <Fragment key={r.name}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text, alignSelf: "center" }}>{r.name}</span>
                    {r.cells.map((x) => {
                      const [bg, fg] = cellBg(x.lt);
                      return (
                        <div key={`${r.name}-${x.col}`} title={`${x.n} órdenes`} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, minHeight: 64,
                          borderRadius: 14, background: bg, color: fg, boxShadow: "-3px -3px 8px rgba(255,255,255,.8), 3px 3px 8px rgba(163,177,198,.5)" }}>
                          <span style={{ fontSize: 16, fontWeight: 800 }}>{x.lt == null ? "—" : f1(x.lt, " h")}</span>
                          <span style={{ fontFamily: MONO, fontSize: 10.5, opacity: 0.85 }}>{x.n ? `12 h · ${fp(x.c12)}` : "sin órdenes"}</span>
                        </div>
                      );
                    })}
                  </Fragment>
                ))}
              </div>
            </div>
          )}
        </section>

        <section style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, color: C.ink }}>Detalle de órdenes</div>
              <div style={cap}>{Dt ? `${fn(Dt.total)} órdenes · de la más lenta a la más rápida` : "—"}</div>
            </div>
            <label style={{ ...well, display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", borderRadius: 12, fontSize: 12, color: C.sub }}>
              <Search size={14} color={C.mute} />
              <input aria-label="Buscar OT o técnico" placeholder="Buscar OT o técnico" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                style={{ border: 0, background: "transparent", outline: "none", font: "500 12px Inter", color: C.text, width: 180 }} />
            </label>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div style={{ minWidth: 1060, display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "grid", gridTemplateColumns: "92px 116px 116px 140px 110px 150px 64px 64px 60px minmax(0,1fr)", gap: 10, padding: "0 12px", fontFamily: MONO, fontSize: 10, letterSpacing: ".06em", color: C.sub }}>
                <span>OT</span><span>CREADA</span><span>CERRADA</span><span>TÉCNICO</span><span>SUCURSAL</span><span>CATEGORÍA</span><span>LT</span><span>LT HL</span><span>T.P.</span><span>METAS 6 · 9 · 12 · 24</span>
              </div>
              {(Dt?.items || []).map((o) => (
                <div key={o.id} style={{ ...well, borderRadius: 12, display: "grid", gridTemplateColumns: "92px 116px 116px 140px 110px 150px 64px 64px 60px minmax(0,1fr)", gap: 10, alignItems: "center", padding: "10px 12px", fontSize: 12 }}>
                  <a href={`/ot-smart/${o.id}`} style={{ fontFamily: MONO, fontWeight: 600, color: C.ink }}>{o.ot}</a>
                  <span style={{ fontFamily: MONO, color: "#334155" }}>{o.creada}</span>
                  <span style={{ fontFamily: MONO, color: "#334155" }}>{o.cerrada}</span>
                  <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.tecnico}>{o.tecnico}</span>
                  <span style={{ color: C.text }}>{o.sucursal}</span>
                  <span style={{ color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={o.tipo_solucion}>{o.categoria}</span>
                  <span style={{ fontWeight: 800, color: ltColor(o.lt) }}>{f1(o.lt, " h")}</span>
                  <span style={{ fontWeight: 700, color: "#334155" }}>{f1(o.hl, " h")}</span>
                  <span style={{ color: C.sub }}>{f1(o.tp, " h")}</span>
                  <span style={{ display: "flex", gap: 6 }}>
                    {o.metas.map((ok, i) => (
                      <span key={i} aria-label={`${["6", "9", "12", "24"][i]} h ${ok ? "cumple" : "no cumple"}`} style={{ minWidth: 22, height: 22, display: "grid", placeItems: "center", borderRadius: 7, fontSize: 11, fontWeight: 800,
                        color: ok ? "#15803D" : "#B91C1C", background: ok ? "#DCFCE7" : "#FEE2E2" }}>{ok ? "✓" : "✕"}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {Dt && Dt.total > Dt.size && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center", fontSize: 12, color: C.sub }}>
              <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ border: 0, padding: "8px 12px", borderRadius: 10, background: C.surf, boxShadow: OUT_SM, cursor: "pointer" }}>Anterior</button>
              Página {page} de {Math.ceil(Dt.total / Dt.size)}
              <button disabled={page >= Math.ceil(Dt.total / Dt.size)} onClick={() => setPage((p) => p + 1)} style={{ border: 0, padding: "8px 12px", borderRadius: 10, background: C.surf, boxShadow: OUT_SM, cursor: "pointer" }}>Siguiente</button>
            </div>
          )}
        </section>
      </main>

      <aside style={{ flex: "1 1 260px", maxWidth: 320, display: "flex", flexDirection: "column", gap: 16 }}>
        <section style={{ ...card, padding: 20, gap: 12 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>Programar envío</div>
          {[["Nombre", "nombre", "text"], ["Hora", "hora", "time"]].map(([l, k, t]) => (
            <label key={k} style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>{l}
              <input type={t} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} style={{ padding: "10px 12px", border: 0, borderRadius: 12, font: "600 13px Inter", color: C.text, background: C.bg, boxShadow: IN_SM }} />
            </label>
          ))}
          <div role="group" aria-label="Frecuencia" style={{ display: "flex", gap: 4, padding: 4, borderRadius: 12, background: C.bg, boxShadow: IN_SM }}>
            {["diario", "semanal", "mensual"].map((f) => (
              <button key={f} onClick={() => setForm({ ...form, frecuencia: f })} style={{ flex: 1, border: 0, cursor: "pointer", padding: "7px 0", borderRadius: 9, fontSize: 12, textTransform: "capitalize",
                fontWeight: form.frecuencia === f ? 700 : 500, color: form.frecuencia === f ? C.ink : C.sub, background: form.frecuencia === f ? C.surf : "transparent", boxShadow: form.frecuencia === f ? OUT_SM : "none" }}>{f}</button>
            ))}
          </div>
          {form.frecuencia === "semanal" && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Día
              <select value={form.dia} onChange={(e) => setForm({ ...form, dia: Number(e.target.value) })} style={{ padding: "10px 12px", border: 0, borderRadius: 12, font: "600 13px Inter", background: C.bg, boxShadow: IN_SM }}>
                {["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"].map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </label>
          )}
          {form.frecuencia === "mensual" && (
            <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Día del mes (1–28)
              <input type="number" min="1" max="28" value={form.dia || 1} onChange={(e) => setForm({ ...form, dia: Number(e.target.value) })} style={{ padding: "10px 12px", border: 0, borderRadius: 12, font: "600 13px Inter", background: C.bg, boxShadow: IN_SM }} />
            </label>
          )}
          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>Destinatarios
            <textarea rows={2} placeholder="correo1@alfanet.net.ec, correo2@…" value={form.destinatarios} onChange={(e) => setForm({ ...form, destinatarios: e.target.value })}
              style={{ padding: "10px 12px", border: 0, borderRadius: 12, font: "500 13px Inter", color: C.text, background: C.bg, boxShadow: IN_SM, resize: "vertical" }} />
          </label>
          <span style={{ fontSize: 11, color: C.sub }}>Envía el Excel con los filtros actuales. Diario: el día anterior · Semanal: últimos 7 días · Mensual: el mes anterior.</span>
          <button onClick={saveSched} style={{ minHeight: 44, border: 0, borderRadius: 14, background: C.lime, color: C.ink, font: "800 13px Inter", cursor: "pointer", boxShadow: "4px 4px 12px rgba(155,160,14,.35), -4px -4px 10px rgba(255,255,255,.9)" }}>Guardar programación</button>
        </section>
        {sched.length > 0 && (
          <section style={{ ...card, padding: 18, gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.ink }}>Programaciones</div>
            {sched.map((s) => (
              <div key={s.id} style={{ ...well, borderRadius: 12, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{s.nombre}</span>
                  <span style={{ display: "flex", gap: 4 }}>
                    <button aria-label="Enviarme una prueba" title="Enviarme una prueba" onClick={async () => { try { const { data: d } = await api.post(`/v1/metricas/lead-time/programaciones/${s.id}/probar`, {}, { timeout: 180000 }); toast.success(`Prueba ${d.resultado}`); } catch (e) { toast.error(errMsg(e, "No se pudo enviar la prueba")); } }}
                      style={{ border: 0, background: "transparent", cursor: "pointer", padding: 4 }}><Send size={14} color={C.ink} /></button>
                    <button aria-label="Eliminar programación" onClick={async () => { await api.delete(`/v1/metricas/lead-time/programaciones/${s.id}`); loadSched(); }}
                      style={{ border: 0, background: "transparent", cursor: "pointer", padding: 4 }}><Trash2 size={14} color={C.bad} /></button>
                  </span>
                </div>
                <span style={{ fontSize: 11, color: C.sub }}>{s.frecuencia} · {s.hora} · {s.vista}</span>
                <span style={{ fontSize: 11, color: C.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={s.destinatarios}>{s.destinatarios}</span>
                {s.ultimo_resultado && <span style={{ fontFamily: MONO, fontSize: 10.5, color: C.mute }}>Último: {s.ultimo_resultado}</span>}
              </div>
            ))}
          </section>
        )}
        <section style={{ padding: 18, borderRadius: 22, background: C.bg, boxShadow: IN, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.ink }}>Cómo se calcula</div>
          <div style={{ fontSize: 11.5, color: "#334155", lineHeight: 1.5 }}>
            Lead Time = cierre aceptado por Odoo − creación de la tarea. Horario laboral = la jornada configurada del técnico en «Horarios de Técnicos»
            (o la de su sucursal; sin jornada: 08:00–17:00). T.P. = tiempo de vuelta en cola después de haber estado en campo. LT real = LT − T.P.
            Metas: 6 y 9 h en horario laboral; 12 y 24 h corridas. Solo tareas realizadas.
          </div>
          <div style={{ fontSize: 11, color: C.mute }}>{opts ? `${(opts.tecnico || []).length} técnicos · ${(opts.sucursal || []).length} sucursales en el periodo` : ""}</div>
        </section>
      </aside>
    </div>
  );
}

/* ── v101.268.0 · Cumplimiento por horario laboral (acumulativo sobre LT HL) + mejores técnicos ── */
const SERIE = ["#0A3747", "#2F5F6E", "#5D8B96", "#A9C08A"];
const heat = (v, lo, hi) => {
  if (v == null || hi == null || lo == null) return "transparent";
  const t = hi === lo ? 1 : (v - lo) / (hi - lo);
  const mix = (a, b, x) => Math.round(a + (b - a) * x);
  const [r, g, b] = t < 0.5 ? [mix(248, 255, t * 2), mix(105, 235, t * 2), mix(107, 132, t * 2)]
    : [mix(255, 99, (t - 0.5) * 2), mix(235, 190, (t - 0.5) * 2), mix(132, 123, (t - 0.5) * 2)];
  return `rgba(${r},${g},${b},.55)`;
};
const th = { textAlign: "left", fontFamily: MONO, fontSize: 10.5, letterSpacing: ".05em", color: C.sub, padding: "8px 10px", whiteSpace: "nowrap" };
const td = { padding: "8px 10px", fontSize: 12.5, color: C.text, whiteSpace: "nowrap" };
const num = { ...td, textAlign: "right", fontFamily: MONO };

function Columnas({ g }) {
  const W = 980, H = 300, L = 44, B = 46, T = 14;
  const n = g.categorias.length, ns = g.series.length;
  const bw = (W - L - 10) / n, cw = Math.min(16, (bw - 14) / ns);
  const y = (v) => T + (H - T - B) * (1 - (v || 0) / 100);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }} role="img" aria-label="Cumplimiento por sucursal">
      {[0, 25, 50, 75, 100].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - 10} y1={y(v)} y2={y(v)} stroke="#CBD5E1" strokeDasharray={v ? "3 4" : ""} />
          <text x={L - 8} y={y(v) + 4} textAnchor="end" fontSize="10" fill={C.sub} fontFamily="JetBrains Mono, monospace">{v}%</text>
        </g>
      ))}
      {g.categorias.map((c, i) => {
        const x0 = L + i * bw + (bw - cw * ns) / 2;
        return (
          <g key={c}>
            {g.series.map((s, j) => (
              <rect key={j} x={x0 + j * cw} y={y(s.valores[i])} width={cw - 2} height={Math.max(0, y(0) - y(s.valores[i]))} rx="2" fill={SERIE[j % 4]}>
                <title>{`${c} · ${s.nombre}: ${fp(s.valores[i])}`}</title>
              </rect>
            ))}
            <text x={L + i * bw + bw / 2} y={H - B + 16} textAnchor="middle" fontSize="10.5" fontWeight={c === "Nacional" ? 800 : 600}
              fill={c === "Nacional" ? C.ink : C.sub}>{c.length > 14 ? `${c.slice(0, 13)}…` : c}</text>
          </g>
        );
      })}
    </svg>
  );
}

function Cumplimiento({ params }) {
  const [u, setU] = useState([6, 9, 12, 24]);
  const [kk, setK] = useState(10);
  const [aplic, setAplic] = useState({ u: [6, 9, 12, 24], k: 10 });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [xl, setXl] = useState(false);
  const q = useMemo(() => ({ ...params, u1: aplic.u[0], u2: aplic.u[1], u3: aplic.u[2], u4: aplic.u[3], k: aplic.k }), [params, aplic]);
  useEffect(() => {
    let cancel = false;
    setBusy(true);
    api.get("/v1/metricas/lead-time/cumplimiento", { params: q, timeout: 180000 })
      .then(({ data: d }) => { if (!cancel) setData(d); })
      .catch((e) => { if (!cancel) toast.error(errMsg(e, "No se pudo calcular el cumplimiento")); })
      .finally(() => { if (!cancel) setBusy(false); });
    return () => { cancel = true; };
  }, [q]);
  const aplicar = () => {
    const us = u.map(Number);
    if (us.some((x) => !(x > 0)) || us.some((x, i) => i && x <= us[i - 1])) { toast.error("Los umbrales deben ser mayores que 0 y crecientes."); return; }
    if (!(Number(kk) >= 0)) { toast.error("k debe ser 0 o mayor."); return; }
    setAplic({ u: us, k: Number(kk) });
  };
  const exportar = async () => {
    setXl(true);
    try {
      const r = await api.get("/v1/metricas/lead-time/cumplimiento.xlsx", { params: q, responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `lead_time_cumplimiento_${params.desde}_${params.hasta}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el Excel"); } finally { setXl(false); }
  };
  const us = data?.umbrales || aplic.u;
  const t1 = data?.tabla_sucursales || [];
  const sucRows = t1.slice(0, Math.max(0, t1.length - 2));
  const rango = us.map((_, i) => {
    const v = sucRows.map((r) => r.pct[i]).filter((x) => x != null);
    return v.length ? [Math.min(...v), Math.max(...v)] : [null, null];
  });
  const maxIdx = Math.max(1, ...(data?.ranking || []).map((r) => r.indice || 0));
  const inp = { width: 64, border: 0, padding: "8px 10px", borderRadius: 11, background: C.bg, boxShadow: IN_SM, fontFamily: MONO, fontSize: 13, color: C.text };
  return (
    <>
      <section style={{ ...card, gap: 12 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-end", justifyContent: "space-between" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: 640 }}>
            <div style={h2}>Cumplimiento por horario laboral</div>
            <div style={cap}>Todos los umbrales se miden sobre el <b>LT en horario laboral (LT HL)</b>: una OT cumple X h si su LT HL ≤ X. Es acumulativo
              (quien cumple {us[0]} h cumple también {us[1]}, {us[2]} y {us[3]} h). Período por fecha de cierre.</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
            {u.map((v, i) => (
              <label key={i} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 600, color: C.sub }}>Umbral {i + 1} (h)
                <input type="number" min="0" step="0.5" value={v} onChange={(e) => setU(u.map((x, j) => (j === i ? e.target.value : x)))} style={inp} /></label>
            ))}
            <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 600, color: C.sub }} title="Constante de credibilidad del índice ajustado">k
              <input type="number" min="0" step="1" value={kk} onChange={(e) => setK(e.target.value)} style={inp} /></label>
            <button onClick={aplicar} style={{ border: 0, cursor: "pointer", padding: "9px 14px", borderRadius: 12, font: "700 13px Inter", color: C.ink, background: C.lime, boxShadow: OUT_SM }}>Recalcular</button>
            <button onClick={exportar} disabled={xl || !data} style={{ border: 0, cursor: "pointer", padding: "9px 14px", borderRadius: 12, font: "600 13px Inter", color: C.ink, background: C.surf, boxShadow: OUT_SM, display: "inline-flex", gap: 6, alignItems: "center" }}>
              {xl ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Excel</button>
          </div>
        </div>
        {busy && <div style={{ ...cap, display: "flex", gap: 6, alignItems: "center" }}><Loader2 size={14} className="animate-spin" /> Calculando…</div>}
        {data && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontFamily: MONO, fontSize: 11.5, color: C.sub }}>
            <span>{fn(data.total_ot)} OT</span>
            <span>k = {data.k}</span>
            <span style={{ color: data.validacion.ok ? C.ok : C.bad, fontWeight: 700 }}>
              {data.validacion.ok ? `✓ Acumulativo: ${data.validacion.regla}` : `✗ No acumulativo en: ${data.validacion.grupos_con_error.join(", ")}`}</span>
            {data.calidad.ot_excluidas > 0 && <span style={{ color: C.warn }}><AlertTriangle size={12} style={{ verticalAlign: -2 }} /> {fn(data.calidad.ot_excluidas)} OT sin técnico válido (fuera del ranking)</span>}
          </div>
        )}
      </section>

      {data && (
        <>
          <section style={card}>
            <div style={h2}>1 · Cumplimiento por sucursal y nacional</div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "separate", borderSpacing: "0 4px", width: "100%", minWidth: 720 }}>
                <thead><tr><th style={th}>SUCURSAL</th><th style={{ ...th, textAlign: "right" }}>N.º OT</th>
                  {us.map((x) => <th key={x} style={{ ...th, textAlign: "right" }}>% ≤ {x} H</th>)}<th style={{ ...th, textAlign: "right" }}>LT HL PROM. (H)</th></tr></thead>
                <tbody>
                  {t1.map((r, i) => {
                    const nac = i >= sucRows.length;
                    return (
                      <tr key={r.sucursal} style={{ background: nac ? "#DCE5EE" : C.bg }}>
                        <td style={{ ...td, fontWeight: nac ? 800 : 600, borderRadius: "10px 0 0 10px" }}>{r.sucursal}</td>
                        <td style={num}>{fn(r.ot)}</td>
                        {r.pct.map((v, j) => <td key={j} style={{ ...num, fontWeight: nac ? 800 : 600, background: nac ? "transparent" : heat(v, ...rango[j]) }}>{fp(v)}</td>)}
                        <td style={{ ...num, borderRadius: "0 10px 10px 0" }}>{f1(r.lt_hl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={cap}>Nacional (promedio de sucursales): promedio simple de los % de cada sucursal; N.º OT sumado. Nacional (ponderado): todas las OT del período, incluida «Sin sucursal».</div>
          </section>

          <section style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={h2}>Cumplimiento por sucursal · {us.map((x) => `≤ ${x} h`).join(" / ")}</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {data.grafico.series.map((s, j) => <span key={s.nombre} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12, color: C.sub }}>
                  <i style={{ width: 12, height: 12, borderRadius: 3, background: SERIE[j % 4] }} />{s.nombre}</span>)}
              </div>
            </div>
            <Columnas g={data.grafico} />
          </section>

          <section style={card}>
            <div style={h2}>2 · Mejor técnico por sucursal</div>
            <div style={cap}>Índice ajustado ≤ {us[0]} h = (a + k × % nacional ≤ {us[0]} h) ÷ (n + k). Con pocas OT el índice se acerca al promedio nacional; con muchas, al % real. Empate: más OT.</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12 }}>
              {data.mejor_tecnico.map((m) => (
                <div key={m.sucursal} style={{ ...well, padding: 14, display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".08em", color: C.sub }}>{m.sucursal}</span>
                  <span style={{ fontSize: 14, fontWeight: 800, color: C.ink }}>{m.tecnico ? `🏆 ${m.tecnico}` : "—"}</span>
                  {m.tecnico && <span style={{ fontSize: 12, color: C.sub }}>Índice <b style={{ color: C.ink }}>{fp(m.indice)}</b> · real {fp(m.pct)} · {fn(m.ot)} OT</span>}
                </div>
              ))}
            </div>
          </section>

          <section style={card}>
            <div style={h2}>3 · Ranking de técnicos por sucursal</div>
            <div style={cap}>Orden dentro de cada sucursal por % ≤ {us[0]} h real (desempate: más OT). El ganador es el de mayor índice ajustado. «Sin sucursal» va al final y no tiene ganador.</div>
            <div style={{ overflowX: "auto", maxHeight: 640 }}>
              <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 980 }}>
                <thead style={{ position: "sticky", top: 0, background: C.surf }}><tr>
                  <th style={th}>SUCURSAL</th><th style={th}>TÉCNICO</th><th style={{ ...th, textAlign: "right" }}>POS. %</th><th style={{ ...th, textAlign: "right" }}>OT</th>
                  <th style={{ ...th, textAlign: "right" }}>OT ≤ {us[0]} H</th><th style={{ ...th, textAlign: "right" }}>% ≤ {us[0]} H REAL</th><th style={th}>ÍNDICE AJUSTADO</th>
                  <th style={{ ...th, textAlign: "right" }}>POS. ÍNDICE</th><th style={{ ...th, textAlign: "right" }}>LT HL PROM.</th><th style={th}>GANADOR</th></tr></thead>
                <tbody>
                  {data.ranking.map((r, i) => {
                    const nueva = i > 0 && data.ranking[i - 1].sucursal !== r.sucursal;
                    return (
                      <tr key={`${r.sucursal}|${r.tecnico}`} style={{ background: r.ganador ? "rgba(224,230,33,.28)" : "transparent", borderTop: nueva ? `2px solid ${C.ink}` : "1px solid #D9E1EA" }}>
                        <td style={{ ...td, color: C.sub }}>{r.sucursal}</td>
                        <td style={{ ...td, fontWeight: r.ganador ? 800 : 600 }}>{r.tecnico}</td>
                        <td style={num}>{r.pos_pct}</td><td style={num}>{fn(r.ot)}</td><td style={num}>{fn(r.ot_ok)}</td><td style={num}>{fp(r.pct)}</td>
                        <td style={{ ...td, minWidth: 160 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ flex: 1, height: 8, borderRadius: 999, background: "#D9E1EA" }}>
                              <div style={{ width: `${Math.max(0, (r.indice || 0) / maxIdx) * 100}%`, height: "100%", borderRadius: 999, background: C.teal }} />
                            </div>
                            <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: C.ink }}>{fp(r.indice)}</span>
                          </div>
                        </td>
                        <td style={num}>{r.pos_indice}</td><td style={num}>{f1(r.lt_hl)}</td>
                        <td style={{ ...td, fontWeight: 800, color: C.ink }}>{r.ganador ? "🏆 MEJOR DE LA SUCURSAL" : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          {data.calidad.excluidas_del_ranking.length > 0 && (
            <section style={{ ...card, gap: 10 }}>
              <div style={h2}>Calidad de datos · OT fuera del ranking de técnicos</div>
              <div style={cap}>Cuentan en las tablas de sucursal y nacional, pero no en el ranking: técnico vacío, «Sin técnico» o un correo en lugar del nombre.</div>
              {data.calidad.excluidas_del_ranking.map((e) => (
                <div key={e.motivo} style={{ fontSize: 12.5, color: C.text }}><b>{e.motivo}</b> · {fn(e.ot)} OT · {Object.entries(e.por_sucursal).map(([s, n]) => `${s}: ${n}`).join(" · ")}</div>
              ))}
            </section>
          )}
        </>
      )}
    </>
  );
}

/* ── Página ── */
export default function MetricasLeadTime() {
  const [vista, setVista] = useState("general");
  const [desde, setDesde] = useState(monthStart());
  const [hasta, setHasta] = useState(iso(new Date()));
  const [flt, setFlt] = useState({});
  const [dash, setDash] = useState(null);
  const [evo, setEvo] = useState(null);
  const [meses, setMeses] = useState(12);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const isDash = !["evolucion", "detalle", "cumplimiento"].includes(vista);
  const params = useMemo(() => ({ desde, hasta, vista: isDash ? vista : "general",
    ...Object.fromEntries(Object.entries(flt).filter(([, v]) => v)) }), [desde, hasta, vista, isDash, flt]);

  useEffect(() => {
    if (vista === "evolucion") return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/v1/metricas/lead-time/dashboard", { params, timeout: 180000 });
        if (!cancel) setDash(data);
      } catch (e) { if (!cancel) toast.error(errMsg(e, "No se pudo cargar el Lead Time")); } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [params, vista]);

  useEffect(() => {
    if (vista !== "evolucion") return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const rest = Object.fromEntries(Object.entries(params).filter(([key]) => key !== "desde" && key !== "hasta"));
        const { data } = await api.get("/v1/metricas/lead-time/evolucion", { params: { ...rest, meses }, timeout: 180000 });
        if (!cancel) setEvo(data);
      } catch (e) { if (!cancel) toast.error(errMsg(e, "No se pudo cargar la evolución")); } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [params, vista, meses]);

  const setPeriod = (p) => {
    const now = new Date();
    const to = iso(now);
    if (p === "7d") setDesde(iso(new Date(now.getTime() - 6 * 864e5)));
    if (p === "mes") setDesde(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
    if (p === "trim") setDesde(iso(new Date(now.getFullYear(), now.getMonth() - 2, 1)));
    if (p === "anio") setDesde(iso(new Date(now.getFullYear(), 0, 1)));
    setHasta(to);
  };
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const resp = await api.get("/v1/metricas/lead-time/export.xlsx", { params, responseType: "blob", timeout: 300000 });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `lead_time_${desde}_${hasta}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el Excel"); } finally { setExporting(false); }
  };
  const k = dash?.kpis;
  const opts = dash?.opciones;

  return (
    <Layout>
      <style>{`@keyframes lt-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
      <div style={{ background: C.bg, fontFamily: "Inter, system-ui, sans-serif", color: C.text, padding: "24px clamp(16px, 3vw, 32px) 48px", minHeight: "100%" }} data-testid="metricas-lead-time">
        <div style={{ maxWidth: 1376, margin: "0 auto", display: "flex", flexDirection: "column", gap: 22 }}>
          <header style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: ".14em", color: C.sub }}>MÉTRICAS · LEAD TIME</span>
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: "-.02em", color: C.ink }}>Lead Time operativo</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.sub }}>Cuánto tardamos desde que se crea la tarea hasta que se cierra, y qué parte cumple las metas de 6, 9, 12 y 24 horas.</p>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              {vista === "evolucion" ? (
                <div role="group" aria-label="Meses" style={{ display: "flex", gap: 4, padding: 5, borderRadius: 14, background: C.bg, boxShadow: IN }}>
                  {[6, 12, 24].map((m) => <Pill key={m} active={meses === m} onClick={() => setMeses(m)}>{m} meses</Pill>)}
                </div>
              ) : (
                <>
                  <div role="group" aria-label="Periodo" style={{ display: "flex", gap: 4, padding: 5, borderRadius: 14, background: C.bg, boxShadow: IN }}>
                    {[["7d", "7 días"], ["mes", "Mes"], ["trim", "Trimestre"], ["anio", "Año"]].map(([p, l]) => <Pill key={p} onClick={() => setPeriod(p)}>{l}</Pill>)}
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.sub }}>
                    <input type="date" aria-label="Desde" value={desde} onChange={(e) => setDesde(e.target.value)} style={{ border: 0, padding: "9px 10px", borderRadius: 12, background: C.bg, boxShadow: IN_SM, fontFamily: "Inter" }} />
                    –
                    <input type="date" aria-label="Hasta" value={hasta} onChange={(e) => setHasta(e.target.value)} style={{ border: 0, padding: "9px 10px", borderRadius: 12, background: C.bg, boxShadow: IN_SM, fontFamily: "Inter" }} />
                  </label>
                </>
              )}
              <button onClick={exportXlsx} disabled={exporting} style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, font: "600 13px Inter", color: C.ink, background: C.surf, padding: "11px 16px", borderRadius: 14, cursor: "pointer", boxShadow: "-4px -4px 10px rgba(255,255,255,.95), 4px 4px 10px rgba(163,177,198,.55)" }}>
                {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Exportar
              </button>
              <button onClick={() => setVista("detalle")} style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, font: "700 13px Inter", color: "#FFF", background: C.ink, padding: "11px 16px", borderRadius: 14, cursor: "pointer", boxShadow: "-4px -4px 10px rgba(255,255,255,.9), 4px 4px 12px rgba(10,55,71,.35)" }}>
                <CalendarClock size={16} color={C.lime} /> Programar envío
              </button>
            </div>
          </header>

          <nav aria-label="Vistas del reporte" style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: 6, borderRadius: 18, background: C.bg, boxShadow: IN }}>
            {VIEWS.map((v) => <Pill key={v.id} active={vista === v.id} onClick={() => setVista(v.id)}>{v.label}</Pill>)}
          </nav>

          <section aria-label="Filtros" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, padding: 18, borderRadius: 22, background: C.surf, boxShadow: OUT }}>
            {FILTERS.map(([key, label]) => (
              <Select key={key} label={label} value={flt[key] || ""} options={opts?.[key]} onChange={(v) => setFlt({ ...flt, [key]: v })} />
            ))}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 11, fontWeight: 600, color: C.sub }}>
              Tarea hecha
              <span style={{ ...well, borderRadius: 12, padding: "10px 12px", fontSize: 13, fontWeight: 600, color: C.text }}>Sí (fijo)</span>
            </div>
          </section>

          {loading && <div style={{ ...cap, display: "flex", alignItems: "center", gap: 8 }}><Loader2 size={14} className="animate-spin" /> Calculando métricas…</div>}

          {isDash && k && (
            <>
              {k.ordenes === 0 && <section style={card}><div style={h2}>Sin órdenes realizadas en el periodo y filtros elegidos.</div></section>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 22 }}>
                <Hero k={k} prev={dash.prev} />
                <Rings k={k} />
                <Kpis k={k} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 22 }}>
                <Spectrum bins={dash.bins} k={k} />
                <Gauges familias={dash.familias} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 22 }}>
                <Pulse heat={dash.heat} />
                <Constellation techs={dash.tecnicos} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(460px, 1fr))", gap: 22 }}>
                <Branches list={dash.sucursales} />
                <Techs list={dash.tecnicos} onDetail={() => setVista("detalle")} />
              </div>
            </>
          )}
          {vista === "evolucion" && evo && <Evolution data={evo} />}
          {vista === "detalle" && <Reporting params={params} opts={opts} />}
          {vista === "cumplimiento" && <Cumplimiento params={params} />}
        </div>
      </div>
    </Layout>
  );
}
